import { CaretDown, Backspace, CheckCircle, Lightbulb, Globe, LockKey } from '@phosphor-icons/react';
import * as React from 'react';

import type { AcceptAndPublishVisibility } from '../context';
import { REASON_TITLE } from '../suggestions-header';
import { StatusSourceSummary } from '../status-source-summary';

import { Button } from '@/components/ui/button';
import { brandShadowEffect } from '@/lib/styles';
import { Spinner } from '@/components/ui/spinner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ButtonGroup } from '@/components/ui/button-group';
import { cn } from '@/lib/utils';

export type SuggestionHeaderAction = 'reject' | 'accept-draft' | 'accept-publish-public' | 'accept-publish-internal';

export interface SuggestionsHeaderToolbarProps {
  isNew: boolean;
  isAccepted?: boolean;
  suggestionsCount: number;
  sourceIcon: React.ReactNode;
  sourceDescription: string | null;
  onSourceLinkClick?: () => void;
  showActionButtons?: boolean;
  suggestionReasonBody?: string;
  hideAcceptAndPublishPublic?: boolean;
  pendingAction?: SuggestionHeaderAction | null;
  onAcceptAndPublish?: (visibility: AcceptAndPublishVisibility) => void;
  onRejectAll: () => void | Promise<void>;
  onAcceptAll: () => void | Promise<void>;
  onAcceptAndPublishAction: (visibility: AcceptAndPublishVisibility) => void | Promise<void>;
}

export function SuggestionsHeaderToolbar({
  isNew,
  isAccepted = false,
  suggestionsCount,
  sourceIcon,
  sourceDescription,
  onSourceLinkClick,
  showActionButtons = true,
  suggestionReasonBody,
  hideAcceptAndPublishPublic = false,
  pendingAction = null,
  onAcceptAndPublish,
  onRejectAll,
  onAcceptAll,
  onAcceptAndPublishAction,
}: SuggestionsHeaderToolbarProps) {
  const [whyOpen, setWhyOpen] = React.useState(false);
  const isActionPending = pendingAction != null;
  const isRejectPending = pendingAction === 'reject';
  const isAcceptPending =
    pendingAction === 'accept-draft' ||
    pendingAction === 'accept-publish-public' ||
    pendingAction === 'accept-publish-internal';

  return (
    <div className="flex-1 px-2 pb-2 flex justify-between items-center">
      <StatusSourceSummary
        isNew={isNew}
        isAccepted={isAccepted}
        editCount={suggestionsCount}
        sourceIcon={sourceIcon}
        sourceDescription={sourceDescription}
        onSourceLinkClick={onSourceLinkClick}
      />
      {showActionButtons && (
        <ButtonGroup
          orientation="horizontal"
          rounded={false}
          aria-label="Suggestion actions"
          aria-busy={isActionPending}
          className="gap-1"
        >
          {suggestionReasonBody && (
            <Popover open={whyOpen} onOpenChange={setWhyOpen}>
              <PopoverTrigger asChild>
                <Button variant="ghost" aria-label={REASON_TITLE} disabled={isActionPending}>
                  <Lightbulb aria-hidden="true" />
                  Why
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                side="bottom"
                className="z-above-modal shadow-md dark:shadow-md-invert border-dark-300"
              >
                <div className="font-bold text-sm">{REASON_TITLE}</div>
                <div className="text-sm text-subtle mt-1">{suggestionReasonBody}</div>
              </PopoverContent>
            </Popover>
          )}
          <Button variant="ghost" disabled={isActionPending} onClick={() => void onRejectAll()}>
            {isRejectPending ? <Spinner className="size-5" /> : <Backspace aria-hidden="true" />}
            {isNew ? 'Reject' : 'Reject all'}
          </Button>
          {onAcceptAndPublish ? (
            <DropdownMenu>
              <div className={cn('flex items-center rounded border border-border bg-primary', brandShadowEffect)}>
                <Button
                  variant="default"
                  elevation="shadow"
                  className="gap-1 rounded-none border-0 bg-transparent shadow-none"
                  disabled={isActionPending}
                  onClick={() => void onAcceptAll()}
                >
                  {isAcceptPending ? <Spinner className="size-5" /> : <CheckCircle aria-hidden="true" />}
                  {isNew ? 'Accept as draft' : 'Accept all as draft'}
                </Button>
                <span className="h-full w-px self-stretch bg-foreground" aria-hidden="true" />
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="default"
                    size="icon"
                    className="size-9 rounded-none border-0 bg-transparent shadow-none"
                    aria-label="More accept options"
                    disabled={isActionPending}
                  >
                    <CaretDown className="size-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </div>
              <DropdownMenuContent
                align="end"
                className="z-above-modal border-none shadow-md dark:shadow-md-invert mt-1 "
              >
                {!hideAcceptAndPublishPublic && (
                  <DropdownMenuItem
                    className="p-3"
                    disabled={isActionPending}
                    onClick={() => void onAcceptAndPublishAction('public')}
                  >
                    <Globe aria-hidden="true" />
                    {isNew ? 'Accept & publish publicly' : 'Accept all & publish publicly'}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  className="p-3"
                  disabled={isActionPending}
                  onClick={() => void onAcceptAndPublishAction('internal')}
                >
                  <LockKey aria-hidden="true" />
                  {isNew ? 'Accept & publish internally' : 'Accept all & publish internally'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button variant="default" elevation="shadow" disabled={isActionPending} onClick={() => void onAcceptAll()}>
              {isAcceptPending ? <Spinner className="size-5" /> : <CheckCircle aria-hidden="true" />}
              {isNew ? 'Accept as draft' : 'Accept all as draft'}
            </Button>
          )}
        </ButtonGroup>
      )}
    </div>
  );
}
